import {PencilIcon} from '@primer/octicons-react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {RenamedTitleEvent$key} from './__generated__/RenamedTitleEvent.graphql'
import styles from './RenamedTitleEvent.module.css'
import {TimelineRow} from './row/TimelineRow'

type RenamedTitleEventProps = {
  queryRef: RenamedTitleEvent$key
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
}

export function RenamedTitleEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
}: RenamedTitleEventProps): React.ReactElement {
  const {actor, createdAt, currentTitle, previousTitle, databaseId} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RenamedTitleEvent on RenamedTitleEvent {
        databaseId
        createdAt
        actor {
          avatarUrl(size: 64)
          login
          profileResourcePath
          __typename
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
          ... on User {
            name
          }
          ... on EnterpriseUserAccount {
            name
          }
          ... on Mannequin {
            name
          }
        }
        currentTitle
        previousTitle
      }
    `,
    queryRef,
  )

  const highlighted = String(databaseId) === highlightedEventId
  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={PencilIcon}
    >
      <TimelineRow.Main>
        {LABELS.timeline.renamedTitle}{' '}
        <span className={styles.defaultColor}>
          <del className={styles.strikeThrough}>
            <span className="sr-only">[-]</span>
            {previousTitle}
            <span className="sr-only">[/-]</span>
          </del>
        </span>{' '}
        <span className={styles.defaultColor}>
          <ins className={styles.noUnderline}>
            <span className="sr-only">[+]</span>
            {currentTitle}
            <span className="sr-only">[/+]</span>
          </ins>
        </span>{' '}
      </TimelineRow.Main>
    </TimelineRow>
  )
}
